import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Zap, Gift } from "lucide-react";
import { HeroAnimatedFlow } from "./HeroAnimatedFlow";

// Product types that rotate with ".ai" suffix
const products = [
  { name: "stickers", color: "text-accent" },
  { name: "coffee mugs", color: "text-blue-500" },
  { name: "tshirts", color: "text-green-500" },
  { name: "hoodies", color: "text-amber-500" },
  { name: "desk mats", color: "text-purple-500" },
  { name: "onesies", color: "text-pink-500" },
];

export function HeroSection({ onCreateDesign }: { onCreateDesign: () => void }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % products.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Safety check to prevent undefined errors
  if (!products || products.length === 0) {
    return null;
  }

  return (
    <section className="pt-4 pb-4 px-4 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background -z-10" />

      <div className="container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <div className="space-y-8 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium ring-1 ring-accent/80">
              <Sparkles className="w-4 h-4 text-accent" />
              <span>Idea to Reality in 4 steps</span>
            </div>

            {/* Dynamic product name with .ai */}
            <h1 className="text-h1 sm:text-display md:text-[4rem] lg:text-[4.5rem] font-bold leading-tight">
              <span className="block mb-2">Design custom</span>
              <div className="inline-flex items-center gap-1 font-sans">
                {/* Screen-reader / crawler text: only the current product */}
                <span className="sr-only">{products[currentIndex]?.name || "products"}</span>
                <div className="relative h-[1.2em] overflow-hidden text-right" aria-hidden="true">
                  <div
                    className="flex flex-col transition-transform duration-500 ease-in-out"
                    style={{
                      transform: `translateY(-${currentIndex * 1.2}em)`,
                    }}
                  >
                    {products.map((product, index) => (
                      <span
                        key={index}
                        className={`h-[1.2em] flex items-center justify-end font-semibold ${product?.color || "text-gray-500"}`}
                        style={{ minWidth: "10.5rem" }}
                      >
                        {product?.name || ""}
                      </span>
                    ))}
                  </div>
                </div>
                <span className="font-semibold text-foreground">by.ai</span>
              </div>
            </h1>

            <p className="text-xl text-muted-foreground leading-relaxed max-w-xl">
              Describe what you want, and our AI creates it in seconds—no design skills needed.
              Start free, no credit card required.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={onCreateDesign}
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white px-8 text-base group"
                data-testid="hero-start-designing"
                aria-label="Turn your idea into a custom product"
              >
                Turn Your Idea Into Reality
                <ArrowRight
                  className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform"
                  aria-hidden="true"
                />
              </Button>
            </div>

            {/* Benefits */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 pt-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
                <div>
                  <div className="text-sm font-semibold text-foreground">AI-Powered</div>
                  <div className="text-xs text-muted-foreground">Design in seconds</div>
                </div>
              </div>
              <div className="hidden sm:block w-px h-10 bg-border" />
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" aria-hidden="true" />
                <div>
                  <div className="text-sm font-semibold text-foreground">No Skills Needed</div>
                  <div className="text-xs text-muted-foreground">Just describe your idea</div>
                </div>
              </div>
              <div className="hidden sm:block w-px h-10 bg-border" />
              <div className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-primary" aria-hidden="true" />
                <div>
                  <div className="text-sm font-semibold text-foreground">Free to Start</div>
                  <div className="text-xs text-muted-foreground">No credit card required</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Animated flow visualization */}
          <div
            className="relative animate-scale-in mt-12 lg:mt-0"
            style={{ animationDelay: "0.2s" }}
          >
            <HeroAnimatedFlow />
          </div>
        </div>
      </div>
    </section>
  );
}
